import { AppConfig } from "../../app-config";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Status, StatusResponse, TaskResponse, TaskType } from "@pienso/ingest-types/task.interface";
import { catchError, distinctUntilChanged, filter, map, scan, share, skipWhile, switchMap, take, tap } from "rxjs/operators";
import { timer, OperatorFunction, Observable, Subject, merge, EMPTY } from "rxjs";
import { Injectable, Signal, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";

@Injectable({ providedIn: "root" })
export class TaskDataService {
  private http = inject(HttpClient);
  private config = inject(AppConfig);

  private endpoint: string;

  private statuses: { [id: string]: StatusResponse } = {};

  private tasks$: Observable<StatusResponse[]>;
  private closed$ = new Subject<StatusResponse[]>();

  constructor() {
    const config = this.config;

    this.endpoint = config.ingestApiEndpoint + "/task/";

    const open$ = timer(0, 5000).pipe(switchMap(() => this.getOpenTasks()));
    this.tasks$ = merge(open$, this.closed$).pipe(
      // tap(responses => console.log(`watch tasks: ${JSON.stringify(responses)}`)),
      share(),
    );
  }

  private httpOptions() {
    return {
      headers: new HttpHeaders({
        "Content-Type": "application/json",
      }),
    };
  }

  public pollTask(
    description: string,
    throwOnFailure = true,
    interval = 2000,
  ): OperatorFunction<TaskResponse, StatusResponse> {
    return switchMap((task: TaskResponse) =>
      timer(0, interval).pipe(
        switchMap(() => this.http.get<StatusResponse>(this.endpoint + task.taskId, this.httpOptions())),
        tap((sr: StatusResponse) => {
          this.statuses[task.taskId] = sr;
        }),
        skipWhile((sr: StatusResponse) => sr.status !== Status.FAILED && sr.status !== Status.COMPLETE),
        take(1),
        tap((sr: StatusResponse) => {
          this.closed$.next([sr]);
          if (throwOnFailure && sr.status === Status.FAILED) {
            throw new Error(`Task failed: ${description}`);
          }
        }),
      ),
    );
  }

  public getOpenTasks(types?: TaskType[]): Observable<StatusResponse[]> {
    const params = types ? new HttpParams().set("types", types.join(",")) : undefined;
    return this.http.get<StatusResponse[]>(this.endpoint + "open", { ...this.httpOptions(), params });
  }

  /**
   * Polls open tasks of the given types and exposes them as `"<type>_<entityId>"` keys, replaced on
   * every poll. Must be called in an injection context: polling stops when that context is destroyed.
   */
  public openTaskKeys(types: TaskType[], interval = 5000): Signal<Set<string>> {
    const keys$ = timer(0, interval).pipe(
      // a failed poll emits nothing, so the last known tasks stay visible until the next success
      switchMap(() => this.getOpenTasks(types).pipe(catchError(() => EMPTY))),
      map((responses) => new Set(responses.map((sr) => `${sr.type}_${sr.entityId}`))),
    );
    return toSignal(keys$, { initialValue: new Set<string>() });
  }

  public watchEntityTasks(entityId: number, types?: TaskType[]): Observable<StatusResponse[]> {
    return this.tasks$.pipe(
      map((responses: StatusResponse[]) => {
        let r = responses.filter((sr) => sr.entityId === entityId);
        if (types) {
          r = r.filter((sr) => types.includes(<TaskType>sr.type));
        }
        return r;
      }),
      distinctUntilChanged(),
      // filter((responses: StatusResponse[]) => !!responses.length)
    );
  }

  // /** Interval in milliseconds. Must be unsubscribed! */
  // public pollOpenTasks(interval: number = 5000, types?: TaskType[]): Observable<StatusResponse[]> {
  //   return timer(0, interval).pipe(
  //     switchMap(() => this.openTasks(types))
  //   );
  // }
}
